import * as i0 from '@angular/core';
import { inject, input, booleanAttribute, computed, output, Component, ChangeDetectionStrategy } from '@angular/core';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { SHARE_BUTTONS_CONFIG, ShareButtonDirective } from 'ngx-sharebuttons';
function ShareButton_Conditional_3_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 3);
    i0.ɵɵelement(1, "fa-icon", 5);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    const btn_r2 = i0.ɵɵreference(1);
    i0.ɵɵadvance();
    i0.ɵɵproperty("icon", ctx_r0.icon() || btn_r2.icon())("fixedWidth", true);
  }
}
function ShareButton_Conditional_4_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 4);
    i0.ɵɵtext(1);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    const btn_r2 = i0.ɵɵreference(1);
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate1(" ", ctx_r0.text() || btn_r2.text(), " ");
  }
}
let ShareButton = /*#__PURE__*/(() => {
  class ShareButton {
    constructor() {
      /** Injected options */
      this.options = inject(SHARE_BUTTONS_CONFIG);
      /** Share button type */
      this.button = input();
      /** The page URL */
      this.url = input();
      /** The title parameter */
      this.title = input();
      /** The description parameter */
      this.description = input();
      /** The image parameter for sharing on Pinterest */
      this.image = input();
      /** The tags parameter for sharing on X and Tumblr */
      this.tags = input();
      /** Sets the fb messenger redirect url to enable sharing on Messenger desktop */
      this.redirectUrl = input();
      /** Show button icon */
      this.showIcon = input(true, {
        transform: booleanAttribute
      });
      /** Show button text */
      this.showText = input(false, {
        transform: booleanAttribute
      });
      /** Button custom text */
      this.text = input();
      /** Button custom icon */
      this.icon = input();
      /** Button theme */
      this.theme = input(this.options.theme);
      /** A flag that indicates if the button's click is disabled */
      this.disabled = input(false, {
        transform: booleanAttribute
      });
      /** Set theme as button class */
      this.classes = computed(() => `sb-button sb-${this.theme()}`);
      /** Stream that emits when share dialog is opened */
      this.opened = output();
    }
    static {
      this.ɵfac = function ShareButton_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || ShareButton)();
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: ShareButton,
        selectors: [["share-button"]],
        hostVars: 2,
        hostBindings: function ShareButton_HostBindings(rf, ctx) {
          if (rf & 2) {
            i0.ɵɵclassMap(ctx.classes());
          }
        },
        inputs: {
          button: [1, "button"],
          url: [1, "url"],
          title: [1, "title"],
          description: [1, "description"],
          image: [1, "image"],
          tags: [1, "tags"],
          redirectUrl: [1, "redirectUrl"],
          showIcon: [1, "showIcon"],
          showText: [1, "showText"],
          text: [1, "text"],
          icon: [1, "icon"],
          theme: [1, "theme"],
          disabled: [1, "disabled"]
        },
        outputs: {
          opened: "opened"
        },
        decls: 5,
        vars: 14,
        consts: [["btn", "shareButton"], ["type", "button", 1, "sb-wrapper", 3, "opened", "shareButton", "url", "image", "title", "description", "tags", "redirectUrl", "disabled"], [1, "sb-content"], [1, "sb-icon"], [1, "sb-text"], [3, "icon", "fixedWidth"]],
        template: function ShareButton_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵelementStart(0, "button", 1, 0);
            i0.ɵɵlistener("opened", function ShareButton_Template_button_opened_0_listener($event) {
              return ctx.opened.emit($event);
            });
            i0.ɵɵelementStart(2, "div", 2);
            i0.ɵɵconditionalCreate(3, ShareButton_Conditional_3_Template, 2, 2, "div", 3);
            i0.ɵɵconditionalCreate(4, ShareButton_Conditional_4_Template, 2, 1, "div", 4);
            i0.ɵɵelementEnd()();
          }
          if (rf & 2) {
            const btn_r2 = i0.ɵɵreference(1);
            i0.ɵɵclassProp("sb-show-icon", ctx.showIcon())("sb-show-text", ctx.showText());
            i0.ɵɵproperty("shareButton", ctx.button())("url", ctx.url())("image", ctx.image())("title", ctx.title())("description", ctx.description())("tags", ctx.tags())("redirectUrl", ctx.redirectUrl())("disabled", ctx.disabled());
            i0.ɵɵadvance(3);
            i0.ɵɵconditional(ctx.showIcon() && btn_r2.icon() ? 3 : -1);
            i0.ɵɵadvance();
            i0.ɵɵconditional(ctx.showText() ? 4 : -1);
          }
        },
        dependencies: [ShareButtonDirective, FaIconComponent],
        styles: ["[button=facebook][_nghost-%COMP%], [button=facebook]   [_nghost-%COMP%]{--button-color: #4267B2}[button=x][_nghost-%COMP%], [button=x]   [_nghost-%COMP%]{--button-color: #00acee}[button=mix][_nghost-%COMP%], [button=mix]   [_nghost-%COMP%]{--button-color: #ff8226}[button=line][_nghost-%COMP%], [button=line]   [_nghost-%COMP%]{--button-color: #00b900}[button=linkedin][_nghost-%COMP%], [button=linkedin]   [_nghost-%COMP%]{--button-color: #0a66c2}[button=pinterest][_nghost-%COMP%], [button=pinterest]   [_nghost-%COMP%]{--button-color: #bd081c}[button=reddit][_nghost-%COMP%], [button=reddit]   [_nghost-%COMP%]{--button-color: #ff4006}[button=tumblr][_nghost-%COMP%], [button=tumblr]   [_nghost-%COMP%]{--button-color: #36465d}[button=whatsapp][_nghost-%COMP%], [button=whatsapp]   [_nghost-%COMP%]{--button-color: #25d366}[button=messenger][_nghost-%COMP%], [button=messenger]   [_nghost-%COMP%]{--button-color: #0080FF}[button=telegram][_nghost-%COMP%], [button=telegram]   [_nghost-%COMP%]{--button-color: #0088cc}[button=xing][_nghost-%COMP%], [button=xing]   [_nghost-%COMP%]{--button-color: #006567}[button=sms][_nghost-%COMP%], [button=sms]   [_nghost-%COMP%]{--button-color: #20c16c}[button=email][_nghost-%COMP%], [button=email]   [_nghost-%COMP%]{--button-color: #FF961C}[button=viber][_nghost-%COMP%], [button=viber]   [_nghost-%COMP%]{--button-color: #665ca7}[button=vk][_nghost-%COMP%], [button=vk]   [_nghost-%COMP%]{--button-color: #4C75A3}[button=copy][_nghost-%COMP%], [button=copy]   [_nghost-%COMP%]{--button-color: #607D8B}[button=print][_nghost-%COMP%], [button=print]   [_nghost-%COMP%]{--button-color: #765AA2}[button=expand][_nghost-%COMP%], [button=expand]   [_nghost-%COMP%]{--button-color: #FF6651}button[_ngcontent-%COMP%]{cursor:pointer;position:relative;outline:0;-webkit-print-color-adjust:exact;margin:var(--sb-margin, .3125em);padding:var(--sb-padding, 0);min-width:var(--sb-min-width, 4.125em);height:var(--sb-height, 2.5em);color:var(--sb-color, #fff);background:var(--sb-background);font-size:var(--sb-font-size, 13px);line-height:var(--sb-line-height, 2.571em);border:var(--sb-border);border-radius:var(--sb-border-radius);transition:var(--sb-transition);box-shadow:var(--sb-box-shadow);text-shadow:var(--sb-text-shadow);overflow:var(--sb-overflow)}.sb-icon[_ngcontent-%COMP%], .sb-text[_ngcontent-%COMP%]{display:flex;align-items:center;justify-content:center;-webkit-user-select:none;user-select:none}.sb-content[_ngcontent-%COMP%]{flex:1;display:flex;height:100%;width:100%;position:relative}.sb-text[_ngcontent-%COMP%]{flex:1;height:100%;white-space:nowrap;padding:var(--sb-text-padding, 0 .7em);font-weight:var(--sb-font-weight, bold)}.sb-icon[_ngcontent-%COMP%]{text-align:center;width:100%;height:100%;min-width:2em;font-size:var(--sb-icon-size, 1.2em)}"]
      });
    }
  }
  return ShareButton;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Generated bundle index. Do not edit.
 */

export { ShareButton };
